import { NavigationBack, Typography } from "@/components";
import { SubscriptionBar } from "@/components/alert";
import FloatingSearchV2 from "@/components/ui/floating-search/v2";
import useProfile from "@/hooks/useProfile";
import type { CaseSearchParamSchema } from "@/schemas";
import AppBar from "@mui/material/AppBar";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Toolbar from "@mui/material/Toolbar";
import { useLocation, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

const TABS_LINKS = {
	case: ["/dashboard/case/active", "/dashboard/case/closed"],
	records: ["/dashboard/case/records", "/dashboard/case/records/closed"],
	"progress-notes": [
		"/dashboard/case/progress-notes",
		"/dashboard/case/progress-notes/closed",
	],
} as const;

interface AppBarCaseProps {
	variant?: keyof typeof TABS_LINKS;
	title?: string;
	info?: string;
}

const AppBarCase = ({ variant = "case", title, info }: AppBarCaseProps) => {
	const { t } = useTranslation("common");
	const displayTitle = title ?? t("cases");
	const searchQ = useSearch({
		select: (state: CaseSearchParamSchema) => state.q ?? "",
	});
	const { profile } = useProfile();
	const navigate = useNavigate();

	const caseLeft = profile?.case_left || 0;

	const activeTab = useLocation({
		select: (state) =>
			TABS_LINKS[variant].findIndex((link) => state.pathname === link) || 0,
	});
	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		event.preventDefault();
		return navigate({
			replace: true,
			to: TABS_LINKS[variant][newValue],
		});
	};
	const onChangeSearch = (search: string) => {
		return navigate({
			replace: true,
			search: {
				// @ts-expect-error
				q: search || undefined,
			},
		});
	};
	return (
		<AppBar
			position="relative"
			color="transparent"
			className="[&_*_]:text-white !bg-gradient-case"
		>
			<div className="container">
				<Toolbar classes={{ root: "!p-0" }}>
					<NavigationBack />
					<div className="w-full relative flex gap-1 justify-between py-3">
						<FloatingSearchV2
							name="search"
							value={searchQ}
							onClear={() => onChangeSearch("")}
							onChange={(e) => onChangeSearch(e.target.value)}
							className="!rounded-xl"
							inputClassName="!rounded-xl"
						>
							<div className="flex flex-col justify-center">
								<Typography fontWeight={700} fontSize={16}>
									{displayTitle}
								</Typography>
								{info ? (
									<Typography fontWeight={400} fontSize={12}>
										{info}
									</Typography>
								) : null}
							</div>
						</FloatingSearchV2>
					</div>
				</Toolbar>
				<Toolbar classes={{ root: "!p-0 !items-end" }}>
					<Tabs
						value={activeTab > 0 ? activeTab : 0}
						onChange={handleChange}
						indicatorColor="primary"
						textColor="inherit"
						variant="fullWidth"
						aria-label="full width tabs example"
						className="w-full [&_span.MuiTabs-indicator]:!bg-white"
					>
						<Tab label={t("active")} sx={{ textTransform: "none" }} />
						<Tab label={t("archived")} sx={{ textTransform: "none" }} />
					</Tabs>
				</Toolbar>
			</div>
			{!profile?._isProPlan && variant === "case" ? (
				<SubscriptionBar
					caseLeft={caseLeft}
					startOn={profile?.subscription_plan?.start_on}
				/>
			) : null}
		</AppBar>
	);
};

export default AppBarCase;
